import { RecurrenceConfig } from "components/tasks/v2/recurrence-config";
import {
    DayOfWeek,
    RecurrencePattern,
    UpdateTaskPayload,
    WorkflowActionTaskEntity,
} from "generated/graphql";
import { produce } from "immer";
import React from "react";
import { EditableWorkflow } from "./types";

interface RecurrenceEditorProps {
    workflow: EditableWorkflow;
    onChange: (workflow: EditableWorkflow) => void;
    editable: boolean;
}

export const RecurrenceEditor: React.FC<RecurrenceEditorProps> = ({
    workflow,
    onChange,
    editable,
}) => {
    const action = workflow.actions[0] as WorkflowActionTaskEntity;

    const handleRecurrenceChange = (changes: Partial<UpdateTaskPayload>) => {
        const newWorkflow = produce(workflow, draft => {
            const action = draft.actions[0] as WorkflowActionTaskEntity;
            if (action) {
                if (changes.recurrencePattern !== undefined) {
                    action.recurrencePattern =
                        changes.recurrencePattern === RecurrencePattern.None
                            ? null
                            : changes.recurrencePattern;
                }
                if (changes.recurrenceInterval !== undefined) {
                    action.recurrenceInterval =
                        changes.recurrenceInterval?.toString() || null;
                }
                if (changes.recurrenceDaysOfWeek !== undefined) {
                    action.recurrenceDaysOfWeek = changes.recurrenceDaysOfWeek
                        ? changes.recurrenceDaysOfWeek.join(",")
                        : null;
                }
                if (changes.recurrenceDayOfMonth !== undefined) {
                    action.recurrenceDayOfMonth =
                        changes.recurrenceDayOfMonth?.toString() || null;
                }
            }
        });
        onChange(newWorkflow);
    };

    const recurrencePattern = action.recurrencePattern
        ? (action.recurrencePattern as RecurrencePattern)
        : RecurrencePattern.None;

    const recurrenceInterval = action.recurrenceInterval
        ? parseInt(action.recurrenceInterval, 10)
        : null;

    const recurrenceDaysOfWeek = action.recurrenceDaysOfWeek
        ? action.recurrenceDaysOfWeek.split(",").map(d => d.trim() as DayOfWeek)
        : null;

    const recurrenceDayOfMonth = action.recurrenceDayOfMonth
        ? parseInt(action.recurrenceDayOfMonth, 10)
        : null;

    return (
        <div className="space-y-3">
            <div>
                <h3 className="font-medium text-gray-900">Recurrence</h3>
                <p className="text-sm text-gray-500">
                    Configure if this task should repeat automatically
                </p>
            </div>

            <div className="space-y-3">
                {editable ? (
                    <RecurrenceConfig
                        recurrencePattern={recurrencePattern}
                        recurrenceInterval={recurrenceInterval}
                        recurrenceDaysOfWeek={recurrenceDaysOfWeek}
                        recurrenceDayOfMonth={recurrenceDayOfMonth}
                        onChange={handleRecurrenceChange}
                    />
                ) : (
                    <div className="text-gray-500 text-sm">
                        Recurrence configuration is read-only in this context
                    </div>
                )}
            </div>
        </div>
    );
};
